import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { IMAGES } from "../siteConfig";

const SERVICES = [
  {
    title: "Estate & land overviews",
    body: "A clear, organised aerial view of the whole property — fields, woodland, access routes, water features and key structures — so the estate can be understood at a glance.",
    outputs: ["High-resolution aerial imagery of the full holding", "Annotated overview highlighting key features", "A structured image set organised by area"],
    useful: "Estate owners and managers who want the whole picture in one place.",
  },
  {
    title: "Aerial imagery & visual records",
    body: "A dated, organised visual record of a site or area of land as it stands — a reference you can return to, share and compare against later.",
    outputs: ["Dated aerial photographs of agreed areas", "Consistent viewpoints for repeat capture", "A simple index so images are easy to file and find"],
    useful: "Keeping records for insurance, planning conversations or handover.",
  },
  {
    title: "Field and site mapping",
    body: "Suitable aerial imagery processed into map-based outputs that show areas, features and spatial relationships clearly. The right scope depends on the site and how the outputs will be used.",
    outputs: ["Orthomosaic-style map imagery of the survey area", "Field and feature boundary overlays", "Simple measurement of visible areas and distances where appropriate"],
    useful: "Reviewing layout, planning work and briefing contractors.",
  },
  {
    title: "Progress & change monitoring",
    body: "Repeat capture at agreed intervals, aligned so visible change is easy to compare — useful for works, restoration, planting and stewardship schemes.",
    outputs: ["Repeat imagery from matched viewpoints", "Side-by-side comparison sets", "A running dated record of visible change"],
    useful: "Tracking works, habitat change or scheme progress over seasons.",
  },
  {
    title: "Mapping support for rural professionals",
    body: "Aerial imagery and agreed mapping outputs supplied to support the work of surveyors, land agents, ecologists, foresters and other professionals — as an input to their expertise, not a replacement for it.",
    outputs: ["Capture to a brief agreed with the instructing professional", "Imagery and map outputs in practical, shareable formats", "Clear notes on capture conditions and limitations"],
    useful: "Professionals who need a current aerial view to support their own assessment.",
  },
];

export default function Services() {
  return (
    <>
      <section data-testid="services-hero" className="bg-forest pb-20 pt-40 text-limestone md:pb-28 md:pt-52">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal><Eyebrow light>Services</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Mapping shaped around what you need to see.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-limestone/80 md:text-lg">
              Five practical services, one principle: agree the outcome first, then capture and process to
              suit. Outputs and accuracy depend on the project, capture method and processing requirements —
              we'll make the scope and limitations clear before any work begins.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-28">
        <div className="space-y-16 md:space-y-20">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={0.05}>
              <article
                data-testid={`service-detail-${i}`}
                className={`grid gap-8 border-t border-forest/15 pt-10 lg:grid-cols-12 ${i % 2 === 1 ? "" : ""}`}
              >
                <div className="lg:col-span-4">
                  <span className="font-serif text-4xl italic text-ochre/70">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 font-serif text-2xl leading-snug text-forest md:text-3xl">{s.title}</h2>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-sage">{s.useful}</p>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-base leading-relaxed text-charcoal/85">{s.body}</p>
                </div>
                <div className="lg:col-span-4">
                  <p className="eyebrow text-forest/70">Possible outputs</p>
                  <ul className="mt-4 space-y-2.5">
                    {s.outputs.map((o) => (
                      <li key={o} className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/80">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ochre" aria-hidden="true" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 overflow-hidden border border-forest/15">
            <img
              src={IMAGES.estate}
              alt="Aerial view across a rural estate with woodland and parkland"
              loading="lazy"
              className="h-64 w-full object-cover md:h-96"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-20 flex flex-col items-start justify-between gap-8 bg-forest p-10 text-limestone md:flex-row md:items-center md:p-14">
            <div>
              <h2 className="font-serif text-2xl leading-snug md:text-3xl">Not sure which fits?</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-limestone/80">
                Describe the land and what you're trying to understand — we'll suggest the most useful approach.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="services-page-cta"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ochre px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-forest-deep transition-colors hover:bg-limestone"
            >
              Discuss your mapping project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
